import { Table } from '@/components/Elements';
import { Heading2 } from '@/components/ui/typography/Typography';
import { EventContext } from '@/features/events/contexts/EventContext';
import { ContractAction, LegacyEventContract } from '@/types';
import _ from 'lodash';
import { useContext } from 'react';

export const EsignTab = () => {
  const { event } = useContext(EventContext);

  if (!event) return <>Loading...</>;

  const hasContracts = (event.contracts?.length ?? 0) > 0;
  const hasLegacyContracts = (event.legacy_contracts?.length ?? 0) > 0;

  if (!hasContracts && !hasLegacyContracts) return <>No contracts found.</>;

  const contractSubmissions =
    _.groupBy(event.contracts ?? [], (contract) => contract.provider_submission_id) ?? {};

  // For each provider submission, we want to find the most recent contract action and display that as the status of the contract. Order descending by created_at.
  const mostRecentContractActions = _.orderBy(
    Object.values(contractSubmissions).map((submissions) => {
      return _.maxBy(submissions, (submission) =>
        new Date(submission.created_at).getTime(),
      ) as ContractAction;
    }),
    [(action) => new Date(action.created_at).getTime()],
    ['desc'],
  );

  return (
    <div>
      {hasContracts && (
        <Table<ContractAction>
          data={mostRecentContractActions ?? []}
          columns={[
            {
              title: 'Documents',
              field: 'attachments',
              Cell({ entry }) {
                return (
                  <div className="flex flex-col space-y-1">
                    {entry.attachments?.length === 0 && (
                      <span>
                        {entry.event_status === 'sent' && (
                          <span className="uppercase">
                            {entry.payload?.context?.category_keys?.join(', ')}
                          </span>
                        )}
                        : Not signed yet
                      </span>
                    )}
                    {entry.attachments?.map((doc, i) => (
                      <a
                        key={`contract-doc-${i}`}
                        href={doc.download_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {doc.name}
                      </a>
                    ))}
                  </div>
                );
              },
            },

            {
              title: 'Status',
              field: 'event_status',
            },
            {
              title: 'Contract ID',
              field: 'provider_submission_id',
            },
          ]}
        />
      )}

      {hasLegacyContracts && (
        <>
          <section>
            <Heading2>Legacy Contracts</Heading2>
          </section>
          <Table<LegacyEventContract>
            data={_.orderBy(event.legacy_contracts ?? [], ['created_at'], ['desc'])}
            columns={[
              {
                title: 'ID',
                field: 'id',
              },
              {
                title: 'Sent',
                field: 'requested_at',
              },
              {
                title: 'Delivered At',
                field: 'delivered_at',
              },
              {
                title: 'Signed',
                field: 'signed_at',
              },
              {
                title: 'Contract Type',
                field: 'types',
                Cell({ entry }) {
                  return <span>{entry.types?.join(', ')}</span>;
                },
              },
            ]}
          />
        </>
      )}
    </div>
  );
};
